/**
 * Metric value resolution shared by all three Data Room renderers: the
 * interactive summary (Vue), the PDF view model, and the XLSX workbook.
 *
 * Deliberately dependency-free so both the client bundle and the Cloudflare
 * Workers renderers can import it without dragging in server contracts or
 * `@lemmonhq/orders`.
 */

export type RatioMetricSpec = {
  key: string;
  /**
   * Renders the metric as a share of another metric in the same object, e.g.
   * discount_rate = discounts / gross_revenue.
   */
  ratioOf?: string;
};

/**
 * The number a metric should actually display, applying `ratioOf` where present.
 *
 * Ratio metrics are derived at render time so SQL keeps returning raw values
 * only — reading `metrics[spec.key]` directly yields the numerator, i.e. a money
 * amount printed where a percentage belongs.
 *
 * Returns `null` when the ratio is undefined (zero denominator) so callers can
 * render a dash instead of a misleading 0%.
 */
export function resolveMetricValue(
  spec: RatioMetricSpec,
  metrics: Record<string, unknown>,
): number | null {
  if (!spec.ratioOf) return Number(metrics[spec.key]) || 0;
  const denominator = Math.abs(Number(metrics[spec.ratioOf]) || 0);
  if (denominator === 0) return null;
  return (Math.abs(Number(metrics[spec.key]) || 0) / denominator) * 100;
}
